import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const CorporateContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectRequirement: '',
    budgetRange: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Inquiry submitted successfully. We will be in touch shortly.');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          projectRequirement: '',
          budgetRange: '',
          timeline: ''
        });
      } else {
        toast.error('Failed to submit inquiry. Please try again.');
      }
    } catch (error) {
      // Error already logged by toast
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 bg-[#0A192F]" id="contact">
      <div className="container mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-rose-500 font-heading mb-4 block">
              Executive Inquiry
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4 font-heading">
              Strategic Partnership Consultation
            </h2>
            <p className="text-base text-slate-300 leading-relaxed font-body mb-8">
              For executive advisory, strategic consulting, or business partnerships, please reach out through the form or contact directly.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-rose-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-rose-500" size={20} />
                </div>
                <div>
                  <div className="text-sm text-slate-400 font-body mb-1">Email</div>
                  <div className="text-base text-white font-body">executive@vijaykadechkarr.com</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-rose-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="text-rose-500" size={20} />
                </div>
                <div>
                  <div className="text-sm text-slate-400 font-body mb-1">Phone</div>
                  <div className="text-base text-white font-body">Available upon request</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-rose-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-rose-500" size={20} />
                </div>
                <div>
                  <div className="text-sm text-slate-400 font-body mb-1">Location</div>
                  <div className="text-base text-white font-body">Mumbai, India</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#112240] border border-white/10 rounded-lg p-6 lg:p-8">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="text-sm text-slate-300 font-body mb-2 block">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="text-sm text-slate-300 font-body mb-2 block">
                    Company / Organization *
                  </label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="text-sm text-slate-300 font-body mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="text-sm text-slate-300 font-body mb-2 block">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectRequirement" className="text-sm text-slate-300 font-body mb-2 block">
                  Project Requirement / Strategic Need *
                </label>
                <Textarea
                  id="projectRequirement"
                  name="projectRequirement"
                  value={formData.projectRequirement}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="Describe your strategic consulting need or partnership opportunity..."
                  className="bg-[#0A192F] border-white/20 text-white font-body resize-none"
                  data-testid="contact-input-requirement"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="budgetRange" className="text-sm text-slate-300 font-body mb-2 block">
                    Budget Range *
                  </label>
                  <Input
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                    required
                    placeholder="e.g., $50K-$100K, To be discussed"
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-budget"
                  />
                </div>
                <div>
                  <label htmlFor="timeline" className="text-sm text-slate-300 font-body mb-2 block">
                    Timeline *
                  </label>
                  <Input
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Immediate, Q1 2025, 3-6 months"
                    className="bg-[#0A192F] border-white/20 text-white font-body"
                    data-testid="contact-input-timeline"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-rose-600 text-white hover:bg-rose-500 text-base py-6 transition-all duration-300 font-semibold font-body"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                <Send className="ml-2" size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CorporateContact;
